import React, { useState, useCallback, useEffect } from 'react';
import { billingAdminService } from '../services/api';
import {
  Receipt, RefreshCw, ChevronLeft, ChevronRight,
  ArrowUpDown, ArrowUp, ArrowDown, Download, X,
  CheckCircle, XCircle, Clock, Zap,
  AlertTriangle, Copy, Filter, RotateCcw, ShieldCheck,
} from 'lucide-react';
import './BillingManagement.css';
import './BillingPages.css';

const fmt = (v) => Number(v || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const fmtINR = (v) => `₹${fmt(v)}`;
const copyId = (text) => { navigator.clipboard.writeText(text).catch(() => {}); };

const relativeDate = (dateStr) => {
  if (!dateStr) return '—';
  const d = new Date(dateStr);
  const now = new Date();
  const diffMs = now - d;
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return 'Just now';
  if (diffMins < 60) return `${diffMins}m ago`;
  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours}h ago`;
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return `${diffDays}d ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: d.getFullYear() !== now.getFullYear() ? 'numeric' : undefined });
};

const fullDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
};

const StatusBadge = ({ status }) => {
  const cfg = {
    success: 'status-active', failed: 'status-disabled', pending: 'status-pending',
    notified: 'status-info', initiated: 'status-info', cancelled: 'status-muted',
    refunded: 'status-pending',
  };
  return <span className={`status-badge ${cfg[status] || ''}`}>{status}</span>;
};

// ── Transaction Detail Drawer ─────────────────────────────────
const TransactionDrawer = ({ txn, onClose, onRefresh }) => {
  const [actionMode, setActionMode] = useState(null); // 'retry' | 'force'
  const [forceResolution, setForceResolution] = useState('failed');
  const [forceReason, setForceReason] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [actionMessage, setActionMessage] = useState(null);

  if (!txn) return null;

  const canRetry = txn.status === 'failed' || txn.status === 'cancelled';
  const canForce = txn.status !== 'success' && txn.status !== 'failed';

  const handleRetry = async () => {
    setActionLoading(true); setActionMessage(null);
    try {
      const result = await billingAdminService.retryTransaction(txn.id);
      setActionMessage({ type: 'success', text: `Retry scheduled. New transaction: ${result.new_transaction?.id?.slice(0, 12)}…` });
      setActionMode(null);
      onRefresh?.();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.response?.data?.error || 'Retry failed' });
    } finally { setActionLoading(false); }
  };

  const handleForceResolve = async () => {
    if (forceReason.trim().length < 5) {
      setActionMessage({ type: 'error', text: 'Reason must be at least 5 characters' });
      return;
    }
    setActionLoading(true); setActionMessage(null);
    try {
      await billingAdminService.forceResolveTransaction(txn.id, { resolution: forceResolution, reason: forceReason });
      setActionMessage({ type: 'success', text: `Transaction force-resolved as ${forceResolution}` });
      setActionMode(null);
      onRefresh?.();
    } catch (err) {
      setActionMessage({ type: 'error', text: err.response?.data?.error || 'Force-resolve failed' });
    } finally { setActionLoading(false); }
  };

  const duration = (start, end) => {
    if (!start || !end) return null;
    const ms = new Date(end) - new Date(start);
    if (ms < 60000) return `${Math.round(ms / 1000)}s`;
    if (ms < 3600000) return `${Math.round(ms / 60000)}m`;
    return `${Math.round(ms / 3600000)}h`;
  };

  return (
    <>
      <div className="detail-drawer-backdrop" onClick={onClose} />
      <div className="detail-drawer">
        <div className="detail-drawer-header">
          <h2><Receipt size={18} /> Transaction Detail</h2>
          <button className="btn btn-secondary btn-sm" onClick={onClose}><X size={14} /></button>
        </div>
        <div className="detail-drawer-body">
          {/* Header section */}
          <div className="detail-drawer-section">
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', marginBottom: '1rem' }}>
              <span style={{ fontSize: '1.75rem', fontWeight: 700 }}>{fmtINR(txn.amount)}</span>
              <StatusBadge status={txn.status} />
            </div>
          </div>

          {/* Account Info */}
          <div className="detail-drawer-section">
            <h3>Account</h3>
            <div className="drawer-detail-grid">
              <div className="drawer-detail-item">
                <label>Tenant</label>
                <span>{txn.tenant_name || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Billing Mode</label>
                <span>{txn.billing_mode || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Account ID</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{txn.billing_account_id?.slice(0, 12)}…</span>
              </div>
              <div className="drawer-detail-item">
                <label>Transaction ID</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{txn.id?.slice(0, 12)}…</span>
              </div>
            </div>
          </div>

          {/* Payment Method */}
          <div className="detail-drawer-section">
            <h3>Payment Method</h3>
            <div className="drawer-detail-grid">
              <div className="drawer-detail-item">
                <label>Method</label>
                <span>{txn.payment_method_label || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Type</label>
                <span>{txn.mandate_type || txn.method_type || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Provider</label>
                <span>{txn.provider || '—'}</span>
              </div>
              <div className="drawer-detail-item">
                <label>Merchant Ref</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{txn.merchant_request_num || '—'}</span>
              </div>
            </div>
          </div>

          {/* Timeline */}
          <div className="detail-drawer-section">
            <h3>Timeline</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                <Zap size={14} style={{ color: '#3498db' }} />
                <span style={{ minWidth: 80, fontSize: '0.8rem', color: '#999' }}>Initiated</span>
                <span style={{ fontSize: '0.875rem' }}>{fullDate(txn.initiated_at)}</span>
              </div>
              {txn.notification_sent_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  <Clock size={14} style={{ color: '#f39c12' }} />
                  <span style={{ minWidth: 80, fontSize: '0.8rem', color: '#999' }}>Notified</span>
                  <span style={{ fontSize: '0.875rem' }}>{fullDate(txn.notification_sent_at)}</span>
                  {duration(txn.initiated_at, txn.notification_sent_at) && (
                    <span style={{ fontSize: '0.75rem', color: '#bbb' }}>+{duration(txn.initiated_at, txn.notification_sent_at)}</span>
                  )}
                </div>
              )}
              {txn.completed_at && (
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                  {txn.status === 'success' ? <CheckCircle size={14} style={{ color: '#27ae60' }} /> : <XCircle size={14} style={{ color: '#dc3545' }} />}
                  <span style={{ minWidth: 80, fontSize: '0.8rem', color: '#999' }}>Completed</span>
                  <span style={{ fontSize: '0.875rem' }}>{fullDate(txn.completed_at)}</span>
                  {duration(txn.initiated_at, txn.completed_at) && (
                    <span style={{ fontSize: '0.75rem', color: '#bbb' }}>+{duration(txn.initiated_at, txn.completed_at)}</span>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Failure Info */}
          {txn.failure_reason && (
            <div className="detail-drawer-section">
              <h3>Failure Info</h3>
              <div style={{ background: '#fff5f5', border: '1px solid #fecaca', borderRadius: 8, padding: '0.75rem', color: '#dc3545', fontSize: '0.875rem' }}>
                <AlertTriangle size={14} style={{ verticalAlign: 'middle', marginRight: 6 }} />
                {txn.failure_reason}
              </div>
            </div>
          )}

          {/* Provider Response */}
          {txn.provider_response && (
            <div className="detail-drawer-section">
              <h3>Provider Response</h3>
              <div className="drawer-json">
                {typeof txn.provider_response === 'string' ? txn.provider_response : JSON.stringify(txn.provider_response, null, 2)}
              </div>
            </div>
          )}

          {/* IDs */}
          <div className="detail-drawer-section">
            <h3>Reference IDs</h3>
            <div className="drawer-detail-grid">
              <div className="drawer-detail-item full-width">
                <label>Transaction ID</label>
                <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                  {txn.id}
                  <button className="copy-btn" onClick={() => copyId(txn.id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                </span>
              </div>
              {txn.provider_txn_id && (
                <div className="drawer-detail-item full-width">
                  <label>Provider Transaction ID</label>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {txn.provider_txn_id}
                    <button className="copy-btn" onClick={() => copyId(txn.provider_txn_id)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                  </span>
                </div>
              )}
              {txn.merchant_request_num && (
                <div className="drawer-detail-item full-width">
                  <label>Merchant Request Number</label>
                  <span style={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                    {txn.merchant_request_num}
                    <button className="copy-btn" onClick={() => copyId(txn.merchant_request_num)} title="Copy" style={{ marginLeft: 4 }}><Copy size={10} /></button>
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Action Buttons */}
          {(canRetry || canForce) && (
            <div className="detail-drawer-section">
              <h3>Actions</h3>
              {actionMessage && (
                <div className={`alert ${actionMessage.type === 'success' ? 'alert-success' : 'alert-error'}`} style={{ marginBottom: '0.75rem', fontSize: '0.85rem' }}>
                  {actionMessage.text}
                </div>
              )}
              {!actionMode && (
                <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                  {canRetry && (
                    <button className="btn btn-primary btn-sm" onClick={() => setActionMode('retry')}>
                      <RotateCcw size={13} /> Retry Charge
                    </button>
                  )}
                  {canForce && (
                    <button className="btn btn-secondary btn-sm" onClick={() => setActionMode('force')} style={{ borderColor: '#f39c12', color: '#f39c12' }}>
                      <ShieldCheck size={13} /> Force Resolve
                    </button>
                  )}
                </div>
              )}
              {actionMode === 'retry' && (
                <div className="action-panel">
                  <div className="action-panel-header">
                    <RotateCcw size={14} /> <strong>Retry Charge</strong>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0' }}>
                    This will create a new transaction for <strong>{fmtINR(txn.amount)}</strong> using the same payment method.
                    The charge will be executed immediately (skipping the 24h notification wait).
                  </p>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setActionMode(null); setActionMessage(null); }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={handleRetry} disabled={actionLoading}>
                      {actionLoading ? <><div className="btn-spinner" /> Retrying…</> : <><RotateCcw size={13} /> Confirm Retry</>}
                    </button>
                  </div>
                </div>
              )}
              {actionMode === 'force' && (
                <div className="action-panel">
                  <div className="action-panel-header">
                    <ShieldCheck size={14} /> <strong>Force Resolve Transaction</strong>
                  </div>
                  <p style={{ fontSize: '0.85rem', color: '#666', margin: '0.5rem 0' }}>
                    Override the natural webhook flow. Use when Easebuzz has confirmed the outcome but the webhook was lost.
                  </p>
                  <div className="form-group" style={{ marginTop: '0.75rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Resolution *</label>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <button className={`btn btn-sm ${forceResolution === 'success' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setForceResolution('success')} type="button">
                        <CheckCircle size={12} /> Mark Success
                      </button>
                      <button className={`btn btn-sm ${forceResolution === 'failed' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setForceResolution('failed')} type="button">
                        <XCircle size={12} /> Mark Failed
                      </button>
                    </div>
                  </div>
                  <div className="form-group" style={{ marginTop: '0.5rem' }}>
                    <label style={{ fontSize: '0.8rem', fontWeight: 600 }}>Reason * (audit trail)</label>
                    <input type="text" value={forceReason} onChange={e => setForceReason(e.target.value)}
                      placeholder="e.g. Confirmed via Easebuzz dashboard, webhook lost…" style={{ fontSize: '0.85rem' }} />
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => { setActionMode(null); setActionMessage(null); }}>Cancel</button>
                    <button className="btn btn-primary btn-sm" onClick={handleForceResolve} disabled={actionLoading}
                      style={{ background: forceResolution === 'success' ? '#27ae60' : '#dc3545', borderColor: forceResolution === 'success' ? '#27ae60' : '#dc3545' }}>
                      {actionLoading ? <><div className="btn-spinner" /> Resolving…</> : <><ShieldCheck size={13} /> Force {forceResolution === 'success' ? 'Success' : 'Fail'}</>}
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// ── CSV Export ─────────────────────────────────────────────────
const exportCSV = (transactions) => {
  const headers = ['Tenant', 'Amount', 'Status', 'Provider', 'Method', 'Merchant Ref', 'Initiated', 'Completed', 'Failure Reason'];
  const rows = transactions.map(t => [
    t.tenant_name, t.amount, t.status, t.provider,
    t.payment_method_label || '', t.merchant_request_num || '',
    t.initiated_at || '', t.completed_at || '', t.failure_reason || '',
  ]);
  const csv = [headers, ...rows].map(r => r.map(c => `"${String(c).replace(/"/g, '""')}"`).join(',')).join('\n');
  const blob = new Blob([csv], { type: 'text/csv' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `transactions-${new Date().toISOString().slice(0, 10)}.csv`; a.click();
  URL.revokeObjectURL(url);
};

// ── Main Page ─────────────────────────────────────────────────
const BillingTransactions = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, pageSize: 25, totalCount: 0, totalPages: 0 });
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [providerFilter, setProviderFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [sorting, setSorting] = useState({ sortBy: 'initiated_at', sortOrder: 'desc' });
  const [selectedTxn, setSelectedTxn] = useState(null);
  const [showFilters, setShowFilters] = useState(false);

  const fetchTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const data = await billingAdminService.getTransactions({
        page: pagination.page, pageSize: pagination.pageSize,
        sortBy: sorting.sortBy, sortOrder: sorting.sortOrder,
        ...(search && { search }), ...(statusFilter && { status: statusFilter }),
        ...(providerFilter && { provider: providerFilter }),
        ...(dateFrom && { from: dateFrom }), ...(dateTo && { to: dateTo }),
      });
      setTransactions(data.transactions || []);
      setSummary(data.summary || null);
      if (data.pagination) setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (err) { console.error('Failed to load transactions:', err); }
    finally { setLoading(false); }
  }, [pagination.page, pagination.pageSize, search, statusFilter, providerFilter, dateFrom, dateTo, sorting]);

  useEffect(() => { fetchTransactions(); }, [fetchTransactions]);

  const handleSort = (field) => {
    setSorting(prev => ({ sortBy: field, sortOrder: prev.sortBy === field && prev.sortOrder === 'asc' ? 'desc' : 'asc' }));
  };
  const getSortIcon = (field) => {
    if (sorting.sortBy !== field) return <ArrowUpDown size={12} className="sort-icon" />;
    return sorting.sortOrder === 'asc' ? <ArrowUp size={12} className="sort-icon active" /> : <ArrowDown size={12} className="sort-icon active" />;
  };

  const showStart = Math.min(((pagination.page - 1) * pagination.pageSize) + 1, pagination.totalCount);
  const showEnd = Math.min(pagination.page * pagination.pageSize, pagination.totalCount);

  return (
    <div className="billing-management">
      <div className="billing-page-header">
        <h1><Receipt size={22} /> Transactions</h1>
        <div className="header-actions">
          <button className="btn btn-secondary btn-sm" onClick={() => setShowFilters(f => !f)} title="Toggle filters">
            <Filter size={14} /> Filters
          </button>
          <button className="btn btn-secondary btn-sm" onClick={fetchTransactions} title="Refresh"><RefreshCw size={14} /></button>
          {transactions.length > 0 && (
            <button className="btn-export" onClick={() => exportCSV(transactions)}><Download size={14} /> Export CSV</button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      {summary && (
        <div className="billing-summary-cards">
          <div className="billing-summary-card">
            <h4><Receipt size={13} /> Total</h4>
            <div className="summary-value">{summary.total.toLocaleString()}</div>
            <div className="summary-sub">transactions found</div>
          </div>
          <div className="billing-summary-card card-success">
            <h4><CheckCircle size={13} /> Successful</h4>
            <div className="summary-value">{summary.successful_count.toLocaleString()}</div>
            <div className="summary-sub">{fmtINR(summary.successful_amount)} collected</div>
          </div>
          <div className="billing-summary-card card-danger">
            <h4><XCircle size={13} /> Failed</h4>
            <div className="summary-value">{summary.failed_count}</div>
            <div className="summary-sub">{summary.failed_count > 0 ? 'Requires attention' : 'All clear'}</div>
          </div>
          <div className="billing-summary-card card-info">
            <h4><Clock size={13} /> Pending</h4>
            <div className="summary-value">{summary.pending_count}</div>
            <div className="summary-sub">in-flight charges</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="billing-filters">
        <input type="text" className="search-input" placeholder="Search by tenant, merchant ref, provider txn ID…"
          value={search} onChange={e => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
        <select value={statusFilter} onChange={e => { setStatusFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Statuses</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
          <option value="pending">Pending</option>
          <option value="notified">Notified</option>
          <option value="initiated">Initiated</option>
          <option value="cancelled">Cancelled</option>
        </select>
        <select value={providerFilter} onChange={e => { setProviderFilter(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}>
          <option value="">All Providers</option>
          <option value="easebuzz">Easebuzz</option>
          <option value="razorpay">Razorpay</option>
          <option value="manual">Manual</option>
        </select>
      </div>

      {showFilters && (
        <div className="billing-filters" style={{ marginTop: '-0.5rem' }}>
          <label style={{ fontSize: '0.8rem', color: '#888' }}>From:</label>
          <input type="date" className="filter-date" value={dateFrom}
            onChange={e => { setDateFrom(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
          <label style={{ fontSize: '0.8rem', color: '#888' }}>To:</label>
          <input type="date" className="filter-date" value={dateTo}
            onChange={e => { setDateTo(e.target.value); setPagination(p => ({ ...p, page: 1 })); }} />
          {(dateFrom || dateTo) && (
            <button className="btn btn-secondary btn-sm" onClick={() => { setDateFrom(''); setDateTo(''); setPagination(p => ({ ...p, page: 1 })); }}>
              <X size={12} /> Clear Dates
            </button>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading"><div className="spinner" /></div>
      ) : transactions.length === 0 ? (
        <div className="billing-card">
          <div className="billing-empty">
            <Receipt size={40} />
            <p>No transactions found</p>
            <span>Transactions appear when charges are initiated against billing accounts</span>
          </div>
        </div>
      ) : (
        <>
          <div className="data-table">
            <table>
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th className="sortable" onClick={() => handleSort('amount')}>Amount {getSortIcon('amount')}</th>
                  <th className="sortable" onClick={() => handleSort('status')}>Status {getSortIcon('status')}</th>
                  <th>Method</th>
                  <th>Provider</th>
                  <th className="sortable" onClick={() => handleSort('initiated_at')}>Initiated {getSortIcon('initiated_at')}</th>
                  <th className="sortable" onClick={() => handleSort('completed_at')}>Completed {getSortIcon('completed_at')}</th>
                  <th>Failure</th>
                </tr>
              </thead>
              <tbody>
                {transactions.map(txn => (
                  <tr key={txn.id} className={`clickable-row ${txn.status === 'failed' ? 'row-failed' : ''}`}
                    onClick={() => setSelectedTxn(txn)}>
                    <td>
                      <div className="user-cell">
                        <div className="user-avatar">{(txn.tenant_name || '?').charAt(0)}</div>
                        <div className="user-info">
                          <span className="user-name">{txn.tenant_name || 'Unknown'}</span>
                          <span className="user-email">{txn.billing_account_id?.slice(0, 8)}…</span>
                        </div>
                      </div>
                    </td>
                    <td><span className="amount-cell">{fmtINR(txn.amount)}</span></td>
                    <td><StatusBadge status={txn.status} /></td>
                    <td>
                      <span style={{ fontSize: '0.85rem' }}>
                        {txn.payment_method_label || '—'}
                      </span>
                    </td>
                    <td><span style={{ fontSize: '0.85rem' }}>{txn.provider || '—'}</span></td>
                    <td className="text-muted">{relativeDate(txn.initiated_at)}</td>
                    <td className="text-muted">{txn.completed_at ? relativeDate(txn.completed_at) : '—'}</td>
                    <td>
                      {txn.failure_reason ? (
                        <span className="failure-text" title={txn.failure_reason}>
                          <XCircle size={11} /> {txn.failure_reason.length > 30 ? txn.failure_reason.slice(0, 30) + '…' : txn.failure_reason}
                        </span>
                      ) : <span className="text-muted">—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {pagination.totalPages > 0 && (
            <div className="pagination">
              <span>Showing {showStart}–{showEnd} of {pagination.totalCount} transactions</span>
              <div className="pagination-buttons">
                <button className="btn btn-secondary btn-sm" disabled={pagination.page <= 1}
                  onClick={() => setPagination(p => ({ ...p, page: p.page - 1 }))}>
                  <ChevronLeft size={14} /> Prev
                </button>
                <button className="btn btn-secondary btn-sm" disabled={pagination.page >= pagination.totalPages}
                  onClick={() => setPagination(p => ({ ...p, page: p.page + 1 }))}>
                  Next <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {/* Detail drawer */}
      {selectedTxn && <TransactionDrawer txn={selectedTxn} onClose={() => setSelectedTxn(null)} onRefresh={() => { setSelectedTxn(null); fetchTransactions(); }} />}
    </div>
  );
};

export default BillingTransactions;
